import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Trip } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardFooter 
} from "@/components/ui/card";
import { 
  Progress 
} from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  PlusIcon, 
  EyeIcon,
  CalendarIcon,
  BarChart3,
  Users,
  MessageSquare,
  PlaneTakeoff,
  Mail,
  CreditCard,
  LayoutDashboard,
  Sparkles,
  ArrowRightIcon,
  Bell,
  TrendingUp,
  Clock,
  Globe,
  AlertCircle,
  CheckCircle2,
  PencilLine,
  BadgeAlert,
  ArrowUpRight,
  Infinity,
  BellRing,
  Zap,
  FileText,
  Target,
  DollarSign,
  Phone,
  Send,
  Calendar,
  UserPlus,
  MapPin,
  Briefcase,
  Star,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Timer,
  Flame,
  Award,
  Rocket
} from "lucide-react";
import { useLocation } from "wouter";
import { FINANCES_PATHS } from "@/lib/financesPaths";
import { format, compareDesc, isFuture, differenceInDays, addDays, isBefore, isToday, isTomorrow, isThisWeek, startOfWeek, endOfWeek } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";

// Define types for our mock data
type PriorityType = 'urgent' | 'high' | 'medium' | 'low';
type ActionItemType = 'booking' | 'proposal' | 'follow-up' | 'payment' | 'marketing' | 'call' | 'meeting';
type NotificationType = 'booking' | 'payment' | 'proposal' | 'message' | 'opportunity';

interface RevenueMetric {
  current: number;
  target: number;
  trend: number;
  period: string;
}

interface ActionItem {
  id: number;
  title: string;
  description?: string;
  dueDate: Date;
  priority: PriorityType;
  type: ActionItemType;
  trip?: string;
  tripId?: number;
  contact?: string;
  contactId?: number;
  amount?: number;
  estimatedRevenue?: number;
  completed?: boolean;
}

interface Opportunity {
  id: number;
  contactName: string;
  contactId: number;
  description: string;
  estimatedValue: number;
  probability: number;
  stage: 'inquiry' | 'proposal' | 'negotiation' | 'closing';
  lastActivity: Date;
  nextAction: string;
  daysInStage: number;
}

interface Notification {
  id: number;
  title: string;
  time: string;
  read: boolean;
  type: NotificationType;
  link: string;
  priority?: PriorityType;
}

// Enhanced mock data focused on revenue and actions
const REVENUE_METRICS = {
  thisMonth: { current: 28450, target: 35000, trend: 16, period: 'This Month' },
  thisQuarter: { current: 78200, target: 100000, trend: 12, period: 'Q1 2024' },
  thisYear: { current: 78200, target: 400000, trend: 8, period: '2024' },
  pipeline: { current: 156000, target: 200000, trend: 24, period: 'Pipeline' }
};

const URGENT_ACTIONS: ActionItem[] = [
  { 
    id: 1, 
    title: 'Call Sarah Miller about Hawaii proposal', 
    description: 'Follow up on $8,500 Hawaii package - proposal sent 3 days ago',
    dueDate: new Date(), 
    contact: 'Sarah Miller',
    contactId: 8,
    priority: 'urgent', 
    type: 'call',
    estimatedRevenue: 8500
  },
  { 
    id: 2, 
    title: 'Confirm Milan hotel reservation', 
    description: 'Client payment received - secure booking before rates increase',
    dueDate: addDays(new Date(), 1), 
    trip: 'Italian Summer',
    tripId: 35,
    priority: 'urgent', 
    type: 'booking',
    estimatedRevenue: 0
  },
  { 
    id: 3, 
    title: 'Send Thompson family proposal', 
    description: 'European tour for 4 - budget $15,000, ready to book',
    dueDate: addDays(new Date(), 1), 
    contact: 'John Thompson',
    contactId: 12,
    priority: 'high', 
    type: 'proposal',
    estimatedRevenue: 15000
  },
  { 
    id: 4, 
    title: 'Payment reminder - Japan trip', 
    description: 'Final payment due in 2 days - $3,200 outstanding',
    dueDate: addDays(new Date(), 2), 
    contact: 'Robert Chen',
    contactId: 15,
    amount: 3200,
    priority: 'high', 
    type: 'payment',
    estimatedRevenue: 3200
  },
  { 
    id: 5, 
    title: 'Schedule consultation - Williams family', 
    description: 'Luxury African safari inquiry - budget $25,000+',
    dueDate: addDays(new Date(), 1), 
    contact: 'David Williams',
    contactId: 22,
    priority: 'high', 
    type: 'meeting',
    estimatedRevenue: 25000
  }
];

const HOT_OPPORTUNITIES: Opportunity[] = [
  {
    id: 1,
    contactName: 'Sarah Miller',
    contactId: 8,
    description: 'Hawaii honeymoon package',
    estimatedValue: 8500,
    probability: 85,
    stage: 'closing',
    lastActivity: addDays(new Date(), -1),
    nextAction: 'Follow up call scheduled',
    daysInStage: 3
  },
  {
    id: 2,
    contactName: 'Thompson Family',
    contactId: 12,
    description: 'European grand tour',
    estimatedValue: 15000,
    probability: 70,
    stage: 'proposal',
    lastActivity: addDays(new Date(), -2),
    nextAction: 'Send detailed proposal',
    daysInStage: 5
  },
  {
    id: 3,
    contactName: 'Williams Family',
    contactId: 22,
    description: 'Luxury African safari',
    estimatedValue: 25000,
    probability: 60,
    stage: 'inquiry',
    lastActivity: new Date(),
    nextAction: 'Schedule consultation',
    daysInStage: 1
  },
  {
    id: 4,
    contactName: 'Martinez Group',
    contactId: 18,
    description: 'Corporate retreat - Costa Rica',
    estimatedValue: 12000,
    probability: 75,
    stage: 'negotiation',
    lastActivity: addDays(new Date(), -1),
    nextAction: 'Revise pricing proposal',
    daysInStage: 7
  }
];

const PRIORITY_NOTIFICATIONS: Notification[] = [
  { 
    id: 1, 
    title: 'New high-value inquiry: Luxury Mediterranean cruise', 
    time: '15 minutes ago', 
    read: false, 
    type: 'opportunity',
    link: '/clients-old/new',
    priority: 'urgent'
  },
  { 
    id: 2, 
    title: 'Payment received: $8,500 from Sarah Miller', 
    time: '1 hour ago', 
    read: false, 
    type: 'payment',
    link: FINANCES_PATHS.invoices,
    priority: 'high'
  },
  { 
    id: 3, 
    title: 'Proposal viewed: Thompson family opened your proposal', 
    time: '2 hours ago', 
    read: false, 
    type: 'proposal',
    link: '/proposals/5',
    priority: 'high'
  },
  { 
    id: 4, 
    title: 'Booking deadline: Milan hotel rates expire tomorrow', 
    time: '3 hours ago', 
    read: false, 
    type: 'booking',
    link: '/trips/35',
    priority: 'urgent'
  }
];

interface RevenueCardProps {
  title: string;
  metric: RevenueMetric;
  icon: React.ReactNode;
  bgColor: string;
  onClick: () => void;
}

const RevenueCard: React.FC<RevenueCardProps> = ({ title, metric, icon, bgColor, onClick }) => {
  const percentage = (metric.current / metric.target) * 100;
  const isOnTrack = percentage >= 75;
  
  return (
    <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4" 
          style={{ borderLeftColor: isOnTrack ? '#10b981' : '#f59e0b' }}
          onClick={onClick}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className={`${bgColor} w-12 h-12 rounded-lg flex items-center justify-center`}>
            {icon}
          </div>
          <div className="text-right">
            <div className={`text-sm font-medium ${isOnTrack ? 'text-emerald-600' : 'text-amber-600'}`}>
              {percentage.toFixed(0)}% of target
            </div>
            <div className={`flex items-center text-xs ${metric.trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {metric.trend > 0 ? '+' : ''}{metric.trend}% vs last period
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">{title}</h3>
            <span className="text-xs text-gray-500">{metric.period}</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            ${metric.current.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Target: ${metric.target.toLocaleString()}
          </div>
          <Progress value={percentage} className="h-2" />
        </div>
      </CardContent>
    </Card>
  );
};

interface ActionItemCardProps {
  item: ActionItem;
  onNavigate: (path: string) => void;
  onComplete: (id: number) => void;
}

const ActionItemCard: React.FC<ActionItemCardProps> = ({ item, onNavigate, onComplete }) => {
  const isOverdue = isBefore(item.dueDate, new Date()) && !isToday(item.dueDate);
  const isDueToday = isToday(item.dueDate);
  const isDueTomorrow = isTomorrow(item.dueDate);
  
  const getPriorityColor = (priority: PriorityType) => {
    switch (priority) {
      case 'urgent': return 'bg-slate-900 text-white border-slate-700';
      case 'high': return 'bg-slate-700 text-white border-slate-600';
      case 'medium': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'low': return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  
  const getTypeIcon = (type: ActionItemType) => {
    switch (type) {
      case 'call': return <Phone className="h-4 w-4" />;
      case 'meeting': return <Calendar className="h-4 w-4" />;
      case 'proposal': return <FileText className="h-4 w-4" />;
      case 'booking': return <PlaneTakeoff className="h-4 w-4" />;
      case 'payment': return <CreditCard className="h-4 w-4" />;
      case 'follow-up': return <Mail className="h-4 w-4" />;
      case 'marketing': return <Sparkles className="h-4 w-4" />;
    }
  };
  
  const getDueDateText = () => {
    if (isOverdue) return 'Overdue';
    if (isDueToday) return 'Due today';
    if (isDueTomorrow) return 'Due tomorrow';
    return `Due ${format(item.dueDate, 'MMM d')}`;
  };
  
  const getDueDateColor = () => {
    if (isOverdue) return 'text-slate-800';
    if (isDueToday) return 'text-slate-700';
    if (isDueTomorrow) return 'text-emerald-700';
    return 'text-gray-600';
  };

  return (
    <Card className={`hover:shadow-md transition-all duration-200 ${isOverdue ? 'border-slate-300 bg-slate-50/50' : isDueToday ? 'border-slate-200 bg-slate-50/30' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`text-xs ${getPriorityColor(item.priority)}`}>
                {item.priority.toUpperCase()}
              </Badge>
              <div className="flex items-center gap-1 text-gray-500">
                {getTypeIcon(item.type)}
                <span className="text-xs capitalize">{item.type}</span>
              </div>
              {item.estimatedRevenue && (
                <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">
                  ${item.estimatedRevenue.toLocaleString()}
                </Badge>
              )}
            </div>
            
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
              {item.title}
            </h4>
            
            {item.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {item.description}
              </p>
            )}
            
            <div className={`text-xs font-medium ${getDueDateColor()}`}>
              <Clock className="h-3 w-3 inline mr-1" />
              {getDueDateText()}
            </div>
          </div>
          
          <div className="flex flex-col gap-2 ml-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (item.contactId) {
                  onNavigate(`/clients-old/${item.contactId}`);
                } else if (item.tripId) {
                  onNavigate(`/trips/${item.tripId}`);
                }
              }}
              className="text-xs"
            >
              <EyeIcon className="h-3 w-3 mr-1" />
              View
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={() => onComplete(item.id)}
              className="text-xs bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="h-3 w-3 mr-1" />
              Done
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface OpportunityCardProps {
  opportunity: Opportunity;
  onNavigate: (path: string) => void;
}

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity, onNavigate }) => {
  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'inquiry': return 'bg-gray-100 text-gray-700';
      case 'proposal': return 'bg-slate-100 text-slate-700';
      case 'negotiation': return 'bg-orange-100 text-orange-800';
      case 'closing': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getProbabilityColor = (probability: number) => {
    if (probability >= 70) return 'text-emerald-600';
    if (probability >= 40) return 'text-orange-600';
    return 'text-slate-600';
  };

  return (
    <Card className="hover:shadow-md transition-all duration-200 cursor-pointer" 
          onClick={() => onNavigate(`/clients-old/${opportunity.contactId}`)}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {opportunity.contactName}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {opportunity.description}
            </p>
          </div>
          <Badge className={`text-xs ${getStageColor(opportunity.stage)}`}>
            {opportunity.stage.toUpperCase()}
          </Badge>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Value:</span>
            <span className="font-medium text-emerald-600">
              ${opportunity.estimatedValue.toLocaleString()}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Probability:</span>
            <span className={`font-medium ${getProbabilityColor(opportunity.probability)}`}>
              {opportunity.probability}%
            </span>
          </div>
          
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Next Action:</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {opportunity.nextAction}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onNavigate: (path: string) => void;
  onMarkRead: (id: number) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onNavigate, onMarkRead }) => {
  const getPriorityIcon = (priority?: PriorityType) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-slate-700" />;
      case 'high': return <AlertCircle className="h-4 w-4 text-slate-600" />;
      default: return <Bell className="h-4 w-4 text-orange-500" />;
    }
  };

  return (
    <div 
      className={`p-3 border-l-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
        notification.read ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-orange-500 bg-orange-50/50 dark:bg-orange-950/20'
      }`}
      onClick={() => {
        onNavigate(notification.link);
        if (!notification.read) onMarkRead(notification.id);
      }}
    >
      <div className="flex items-start gap-3">
        {getPriorityIcon(notification.priority)}
        <div className="flex-1">
          <p className={`text-sm ${notification.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
            {notification.title}
          </p>
          <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
        </div>
        {!notification.read && (
          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [completedActions, setCompletedActions] = useState<number[]>([]);
  const [readNotifications, setReadNotifications] = useState<number[]>([]);

  // Fetch trips
  const { data: tripsResponse = [], isError } = useQuery<Trip[]>({
    queryKey: ["/api/trips"],
    retry: false
  });
  
  // Handle error separately if needed
  if (isError) {
    console.error("Could not load trips");
  }
  
  // Ensure we always have an array of trips
  const trips = Array.isArray(tripsResponse) ? tripsResponse : [];

  // Filter active actions
  const activeActions = URGENT_ACTIONS.filter(action => !completedActions.includes(action.id));
  const activeNotifications = PRIORITY_NOTIFICATIONS.map(notif => ({
    ...notif,
    read: notif.read || readNotifications.includes(notif.id)
  }));

  // Calculate total pipeline value
  const totalPipelineValue = HOT_OPPORTUNITIES.reduce((sum, opp) => sum + (opp.estimatedValue * opp.probability / 100), 0);
  
  // Calculate this week's potential revenue
  const thisWeekRevenue = activeActions
    .filter(action => action.estimatedRevenue && isThisWeek(action.dueDate, { weekStartsOn: 1 }))
    .reduce((sum, action) => sum + (action.estimatedRevenue || 0), 0);

  const handleCompleteAction = (actionId: number) => {
    setCompletedActions(prev => [...prev, actionId]);
    toast({
      title: "Action completed!",
      description: "Great job staying on top of your tasks.",
    });
  };

  const handleMarkNotificationRead = (notificationId: number) => {
    setReadNotifications(prev => [...prev, notificationId]);
  };

  return (
    <div className="h-full flex flex-col overflow-auto bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 pt-6 pb-6 px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Revenue Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Welcome back, {user?.displayName || "User"}! Here's what needs your attention to drive revenue.
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/clients-old/new')} className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="h-4 w-4 mr-2" />
              New Contact
            </Button>
            <Button onClick={() => navigate('/trips/new')} className="bg-emerald-600 hover:bg-emerald-700">
              <PlaneTakeoff className="h-4 w-4 mr-2" />
              New Trip
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 p-6 space-y-6">
        {/* Revenue Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <RevenueCard 
            title="Revenue This Month" 
            metric={REVENUE_METRICS.thisMonth}
            icon={<DollarSign className="h-6 w-6 text-white" />} 
            bgColor="bg-gradient-to-r from-emerald-500 to-emerald-600" 
            onClick={() => navigate("/sales-plans")}
          />
          <RevenueCard 
            title="Quarterly Progress" 
            metric={REVENUE_METRICS.thisQuarter}
            icon={<TrendingUp className="h-6 w-6 text-white" />} 
            bgColor="bg-gradient-to-r from-orange-500 to-orange-600" 
            onClick={() => navigate("/reporting")}
          />
          <RevenueCard 
            title="Annual Target" 
            metric={REVENUE_METRICS.thisYear}
            icon={<Target className="h-6 w-6 text-white" />} 
            bgColor="bg-gradient-to-r from-slate-500 to-slate-600" 
            onClick={() => navigate("/sales-plans")}
          />
          <RevenueCard 
            title="Pipeline Value" 
            metric={REVENUE_METRICS.pipeline}
            icon={<Briefcase className="h-6 w-6 text-white" />} 
            bgColor="bg-gradient-to-r from-slate-600 to-slate-700" 
            onClick={() => navigate("/sales-pipeline")}
          />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-700">This Week's Potential</p>
                  <p className="text-2xl font-bold text-emerald-800">
                    ${thisWeekRevenue.toLocaleString()}
                  </p>
                </div>
                <Flame className="h-8 w-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700">Hot Opportunities</p>
                  <p className="text-2xl font-bold text-orange-800">
                    {HOT_OPPORTUNITIES.length}
                  </p>
                </div>
                <Star className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-700">Urgent Actions</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {activeActions.filter(a => a.priority === 'urgent').length}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-slate-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Action Areas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Urgent Actions */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-red-500" />
                  <CardTitle className="text-xl">Action Required</CardTitle>
                  <Badge variant="destructive" className="text-xs">
                    {activeActions.length} items
                  </Badge>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate("/tasks")}>
                  View All Tasks
                </Button>
              </div>
              <CardDescription>
                High-priority actions that will directly impact your revenue
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {activeActions.slice(0, 5).map((action) => (
                <ActionItemCard 
                  key={action.id} 
                  item={action} 
                  onNavigate={navigate}
                  onComplete={handleCompleteAction}
                />
              ))}
              {activeActions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">All caught up!</p>
                  <p className="text-sm">No urgent actions at the moment.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Priority Notifications */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-blue-500" />
                  <CardTitle>Priority Alerts</CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    {activeNotifications.filter(n => !n.read).length} new
                  </Badge>
                </div>
              </div>
              <CardDescription>
                Important updates and opportunities
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-0">
                {activeNotifications.slice(0, 6).map((notification) => (
                  <NotificationItem 
                    key={notification.id} 
                    notification={notification} 
                    onNavigate={navigate}
                    onMarkRead={handleMarkNotificationRead}
                  />
                ))}
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full" onClick={() => navigate("/notifications")}>
                View All Notifications
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Hot Opportunities */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-xl">Hot Opportunities</CardTitle>
                <Badge className="text-xs bg-blue-100 text-blue-800">
                  ${totalPipelineValue.toLocaleString()} potential
                </Badge>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/sales-pipeline")}>
                Manage Pipeline
              </Button>
            </div>
            <CardDescription>
              High-value prospects that need immediate attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {HOT_OPPORTUNITIES.map((opportunity) => (
                <OpportunityCard 
                  key={opportunity.id} 
                  opportunity={opportunity} 
                  onNavigate={navigate}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-500" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Common tasks to keep your business moving
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => navigate('/clients-old/new')}>
                <UserPlus className="h-5 w-5" />
                <span className="text-xs">Add Contact</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => navigate('/trips/new')}>
                <PlaneTakeoff className="h-5 w-5" />
                <span className="text-xs">New Trip</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => navigate('/threads')}>
                <Sparkles className="h-5 w-5" />
                <span className="text-xs">AI Assistant</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => navigate('/sales-plans')}>
                <Target className="h-5 w-5" />
                <span className="text-xs">Sales Plan</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => navigate('/marketing')}>
                <Mail className="h-5 w-5" />
                <span className="text-xs">Marketing</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => navigate('/reporting')}>
                <BarChart3 className="h-5 w-5" />
                <span className="text-xs">Reports</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
